import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';

interface Source { title: string; url: string }
interface Turn { q: string; a: string; sources: Source[] }

const HISTORY_KEY = 'chat_history';
const WELCOME_KEY = 'chat_welcome_dismissed';
// Ouvre le chat depuis un autre composant : window.dispatchEvent(new Event(OPEN_CHAT))
export const OPEN_CHAT = 'open-chat';

const read = <T,>(get: () => T | null, fallback: T): T => { try { return get() ?? fallback; } catch { return fallback; } };
const linkClass = 'font-semibold text-primary-light underline underline-offset-2';

const EMAIL = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;

// Rendu minimal du Markdown renvoyé : liens [t](/x), [t](https://…) ou [t](mailto:…), **gras**, adresses mail
function renderAnswer(text: string, onNavigate: () => void): ReactNode[] {
  return text.replace(/^ *[*-] +/gm, '• ').split(/(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g).map((part, i) => {
    if (EMAIL.test(part)) return <a key={i} href={`mailto:${part}`} className={linkClass}>{part}</a>;
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const label = link[1];
      // Le modèle recopie parfois l'URL avec des crochets : (/[formations#x])
      const url = link[2].replace(/[[\]<>]/g, '');
      if (url.startsWith('/')) return <Link key={i} to={url} onClick={onNavigate} className={linkClass}>{label}</Link>;
      if (url.startsWith('https://')) return <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={linkClass}>{label}</a>;
      if (EMAIL.test(url.replace(/^mailto:/, ''))) return <a key={i} href={url.startsWith('mailto:') ? url : `mailto:${url}`} className={linkClass}>{label}</a>;
      return <span key={i}>{label}</span>;
    }
    if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    return <span key={i}>{part}</span>;
  });
}

const CloseIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

export default function ChatWidget() {
  const { t: tr, i18n } = useTranslation();
  const t = (key: string) => tr(key) as string;
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(false);
  const [turns, setTurns] = useState<Turn[]>(() => read(() => JSON.parse(sessionStorage.getItem(HISTORY_KEY) || 'null'), []));
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hidden = /^\/(admin|login)/.test(pathname) || /\/(new|edit)(\/|$)/.test(pathname);

  useEffect(() => {
    const onOpen = () => { setOpen(true); setWelcome(false); };
    window.addEventListener(OPEN_CHAT, onOpen);
    return () => window.removeEventListener(OPEN_CHAT, onOpen);
  }, []);

  useEffect(() => {
    if (read(() => localStorage.getItem(WELCOME_KEY), '')) return;
    const timer = setTimeout(() => setWelcome(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(turns)); } catch {}
  }, [turns]);

  useEffect(() => {
    // scrollIntoView ferait aussi défiler la page derrière
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, turns, pending]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const dismissWelcome = () => {
    setWelcome(false);
    try { localStorage.setItem(WELCOME_KEY, '1'); } catch {}
  };
  const openChat = () => { setOpen(true); if (welcome) dismissWelcome(); };
  // Sur mobile le panneau couvre la page : on le ferme quand on suit un lien
  const onNavigate = () => { if (window.innerWidth < 640) setOpen(false); };

  const ask = async (raw: string) => {
    const q = raw.trim();
    if (!q || pending) return;
    setQuestion('');
    setError('');
    setPending(q);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, lang, history: turns.map(({ q, a }) => ({ q, a: a.slice(0, 2000) })) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.answer) throw new Error(t(res.status === 429 ? 'chat_rate_limited' : 'chat_error'));
      setTurns(prev => [...prev, { q, a: data.answer, sources: data.sources ?? [] }].slice(-3));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t('chat_error'));
      setQuestion(q);
    } finally {
      setPending(null);
    }
  };

  const onSubmit = (e: FormEvent) => { e.preventDefault(); ask(question); };
  const suggestions = [t('chat_suggest_1'), t('chat_suggest_2'), t('chat_suggest_3')];
  const userBubble = 'self-end max-w-[85%] rounded-2xl rounded-br-md bg-primary text-white px-4 py-2.5 text-sm selection:bg-white selection:text-primary';
  const botBubble = 'self-start max-w-[92%] rounded-2xl rounded-bl-md bg-surface px-4 py-3 text-sm leading-relaxed';

  if (hidden) return null;

  return (
    <div className="fixed z-50 inset-0 pointer-events-none">
      {open ? (
        <section
          role="dialog"
          aria-label={t('chat_title')}
          className="pointer-events-auto absolute inset-0 sm:inset-auto sm:right-6 sm:bottom-6 sm:w-[384px] sm:h-[600px] sm:max-h-[calc(100vh-48px)] flex flex-col bg-base text-ink sm:rounded-3xl sm:border sm:border-line shadow-2xl overflow-hidden"
        >
          <header className="flex items-center gap-3 px-4 py-3 border-b border-line">
            <span aria-hidden="true" className="flex-none w-10 h-10 rounded-full bg-primary text-white grid place-items-center font-display font-extrabold">N</span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold leading-tight">{t('chat_title')}</p>
              <p className="text-xs text-muted truncate">{t('chat_subtitle')}</p>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label={t('chat_close')} className="flex-none w-11 h-11 rounded-full grid place-items-center bg-surface hover:bg-surface-2">
              <CloseIcon />
            </button>
          </header>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4" aria-live="polite">
            {turns.length === 0 && !pending && <p className={botBubble}>{t('chat_intro')}</p>}
            {turns.map((turn, i) => (
              <div key={i} className="flex flex-col gap-2">
                <p className={userBubble}>{turn.q}</p>
                <div className={`${botBubble} whitespace-pre-line`}>{renderAnswer(turn.a, onNavigate)}</div>
                {turn.sources.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted mr-1">{t('chat_sources')}</span>
                    {turn.sources.map(s => (
                      <Link key={s.url} to={s.url} onClick={onNavigate} className="inline-flex items-center min-h-[32px] px-3 rounded-full border border-line text-xs font-medium hover:bg-surface">
                        {s.url === '/' ? t('chat_profile') : s.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {pending && (
              <div className="flex flex-col gap-2">
                <p className={userBubble}>{pending}</p>
                <p className={`${botBubble} flex items-center gap-1.5 py-4`} role="status" aria-label={t('chat_thinking')}>
                  {[0, 150, 300].map(d => <span key={d} className="w-2 h-2 rounded-full bg-muted animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                </p>
              </div>
            )}
            {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            {turns.length === 0 && !pending && (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map(s => (
                  <button key={s} type="button" onClick={() => ask(s)} className="inline-flex items-center min-h-[36px] px-3.5 rounded-full border border-line text-[13px] font-medium hover:bg-surface">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="flex items-center gap-2 px-3 pt-3 border-t border-line">
            <label htmlFor="chat-q" className="sr-only">{t('chat_placeholder')}</label>
            <input
              id="chat-q"
              ref={inputRef}
              value={question}
              onChange={e => setQuestion(e.target.value)}
              maxLength={500}
              autoComplete="off"
              placeholder={t('chat_placeholder')}
              className="flex-1 min-w-0 h-11 px-4 rounded-full bg-surface text-ink text-sm placeholder:text-muted outline-none focus:ring-2 focus:ring-primary-light"
            />
            <button type="submit" disabled={!question.trim() || !!pending} aria-label={t('chat_send')} className="flex-none w-11 h-11 rounded-full grid place-items-center bg-primary text-white disabled:opacity-40">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </button>
          </form>
          <p className="px-4 pt-2 pb-3 text-[11px] text-muted">{t('chat_privacy')}</p>
        </section>
      ) : (
        <div className="pointer-events-auto absolute right-4 bottom-4 sm:right-6 sm:bottom-6 flex flex-col items-end gap-3">
          {welcome && (
            <div className="relative w-[260px] rounded-2xl rounded-br-md bg-base border border-line shadow-xl text-sm text-ink">
              <button type="button" onClick={openChat} className="block w-full text-left p-4 pr-10">{t('chat_welcome')}</button>
              <button type="button" onClick={dismissWelcome} aria-label={t('chat_dismiss')} className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full grid place-items-center text-muted hover:bg-surface">
                <CloseIcon size={14} />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={openChat}
            aria-label={t('chat_open')}
            className="w-14 h-14 rounded-full grid place-items-center bg-primary text-white shadow-lg hover:bg-primary-dark transition-colors"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}
